import { useEffect, useRef, useState } from "react";

interface PlayerProps {
  title: string;
  poster: string;
  subtitle?: string;
}

type PlayState = "loading" | "playing" | "paused" | "error";

export default function Player({ title, poster, subtitle }: PlayerProps) {
  const [state, setState] = useState<PlayState>("loading");
  const [progress, setProgress] = useState(0);
  const [toast, setToast] = useState("");
  const timerRef = useRef<number | null>(null);

  const startLoading = (forceError = false) => {
    setState("loading");
    setProgress(0);
    if (timerRef.current) window.clearInterval(timerRef.current);
    window.setTimeout(() => {
      setState(forceError ? "error" : "playing");
    }, 1200);
  };

  useEffect(() => {
    startLoading(false);
    return () => {
      if (timerRef.current) window.clearInterval(timerRef.current);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [title, subtitle]);

  useEffect(() => {
    if (timerRef.current) window.clearInterval(timerRef.current);
    if (state === "playing") {
      timerRef.current = window.setInterval(() => {
        setProgress((p) => {
          if (p >= 100) {
            if (timerRef.current) window.clearInterval(timerRef.current);
            return 100;
          }
          return p + 0.4;
        });
      }, 100);
    }
    return () => {
      if (timerRef.current) window.clearInterval(timerRef.current);
    };
  }, [state]);

  const showToast = (msg: string) => {
    setToast(msg);
    window.setTimeout(() => setToast(""), 1800);
  };

  const togglePlay = () => {
    if (state === "playing") setState("paused");
    else if (state === "paused" || state === "error") startLoading(false);
  };

  return (
    <div className="overflow-hidden rounded-lg border border-background-200/70 bg-black">
      <div className="relative aspect-video w-full bg-black">
        <img
          src={poster}
          alt={`${title} 播放画面`}
          className={`h-full w-full object-cover object-top transition ${
            state === "playing" ? "opacity-70" : "opacity-45"
          }`}
        />

        {state === "loading" && (
          <div className="absolute inset-0 flex flex-col items-center justify-center gap-3 bg-black/60">
            <span className="h-10 w-10 animate-spin rounded-full border-2 border-background-400/40 border-t-primary-500"></span>
            <span className="text-sm text-foreground-700">正在加载演示画面…</span>
          </div>
        )}

        {state === "error" && (
          <div className="absolute inset-0 flex flex-col items-center justify-center gap-3 bg-black/70 text-center">
            <i className="ri-error-warning-line text-3xl text-primary-500"></i>
            <span className="text-sm text-foreground-800">演示播放加载失败</span>
            <button
              type="button"
              onClick={() => startLoading(false)}
              className="mt-1 cursor-pointer whitespace-nowrap rounded-md bg-primary-500 px-5 py-2 text-sm font-medium text-background-50 transition hover:bg-primary-600"
            >
              重新加载
            </button>
          </div>
        )}

        {(state === "playing" || state === "paused") && (
          <button
            type="button"
            onClick={togglePlay}
            className="absolute inset-0 flex cursor-pointer items-center justify-center"
            aria-label={state === "playing" ? "暂停" : "播放"}
          >
            <span className="flex h-16 w-16 items-center justify-center rounded-full bg-primary-500/95 text-background-50 transition hover:scale-105">
              <i className={state === "playing" ? "ri-pause-fill text-3xl" : "ri-play-fill text-3xl"}></i>
            </span>
          </button>
        )}

        <div className="absolute left-4 top-4 rounded-md bg-black/60 px-3 py-1 text-xs text-foreground-800">
          {subtitle || "正片演示"}
        </div>
      </div>

      <div className="flex items-center gap-4 bg-background-100 px-4 py-3">
        <button
          type="button"
          onClick={togglePlay}
          className="flex h-9 w-9 cursor-pointer items-center justify-center rounded-md bg-primary-500 text-background-50"
          aria-label="播放控制"
        >
          <i className={state === "playing" ? "ri-pause-fill text-lg" : "ri-play-fill text-lg"}></i>
        </button>
        <div className="h-1.5 flex-1 overflow-hidden rounded-full bg-background-300">
          <div
            className="h-full rounded-full bg-primary-500 transition-all"
            style={{ width: `${progress}%` }}
          />
        </div>
        <span className="w-24 text-right text-xs text-foreground-500">
          {Math.floor(progress * 0.6)}:00 / 60:00
        </span>
        <button
          type="button"
          onClick={() => showToast("演示环境暂不支持音量调节")}
          className="flex h-9 w-9 cursor-pointer items-center justify-center rounded-md text-foreground-600 transition hover:text-primary-400"
          aria-label="音量"
        >
          <i className="ri-volume-up-line text-lg"></i>
        </button>
        <button
          type="button"
          onClick={() => showToast("演示环境暂不支持全屏播放")}
          className="flex h-9 w-9 cursor-pointer items-center justify-center rounded-md text-foreground-600 transition hover:text-primary-400"
          aria-label="全屏"
        >
          <i className="ri-fullscreen-line text-lg"></i>
        </button>
        <button
          type="button"
          onClick={() => startLoading(true)}
          className="hidden cursor-pointer whitespace-nowrap rounded-md border border-background-200 px-3 py-1.5 text-xs text-foreground-600 transition hover:border-primary-300 hover:text-primary-400 md:block"
        >
          模拟加载失败
        </button>
      </div>

      <div className="flex items-start gap-2 border-t border-background-200/60 bg-secondary-950 px-4 py-3">
        <i className="ri-information-line mt-0.5 text-foreground-500"></i>
        <p className="text-xs leading-relaxed text-foreground-500">
          注释：播放功能为前端模拟，仅供演示，真实播放需后端支持。当前播放区域为虚拟 UI 演示，不接入任何实际视频流。
        </p>
      </div>

      {toast && (
        <div className="pointer-events-none fixed bottom-8 left-1/2 z-[70] -translate-x-1/2 rounded-md bg-background-200 px-4 py-2 text-sm text-foreground-900">
          {toast}
        </div>
      )}
    </div>
  );
}